import { rtkApi } from '../rtkApi';

export interface SubscriptionStatus {
  userType: 'free' | 'investor' | 'admin';
  /** JWT-backed role from user record (admin bypasses investor paywall server-side). */
  role?: string;
  investorAccessGranted?: boolean;
  plan: string;
  status: 'active' | 'cancelled' | 'expired' | 'trial' | 'pending' | 'denied';
  startDate?: string;
  endDate?: string;
  stripeSubscriptionId?: string;
  subscription?: Subscription;
}

export interface Subscription {
  _id: string;
  userId: string;
  plan: string;
  status: string;
  startDate?: string;
  endDate?: string;
  amount: number;
  billingCycle: string;
  paymentMethod: string;
  provider?: 'manual' | 'stripe';
  checkoutSessionId?: string;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CheckoutSessionRequest {
  plan: 'investor_monthly';
  successUrl: string;
  cancelUrl: string;
}

export interface CheckoutSessionResponse {
  sessionId: string;
  url: string;
  provider?: 'manual' | 'stripe';
}

export interface AdminSubscriptionRow extends Subscription {
  user?: { _id: string; name?: string; email?: string; role?: string } | null;
  decisionNote?: string;
  decidedAt?: string;
  requestedAt?: string;
}

export interface LoanProgramAccessResponse {
  success: boolean;
  hasAccess: boolean;
  message?: string;
  upgradeRequired?: boolean;
}

export const subscriptionApi = rtkApi.injectEndpoints({
  endpoints: (builder) => ({
    // Create Stripe checkout session
    createCheckoutSession: builder.mutation<
      CheckoutSessionResponse,
      CheckoutSessionRequest
    >({
      query: (data) => ({
        url: '/subscription/create-checkout-session',
        method: 'POST',
        data,
      }),
      transformResponse: (response: {
        success: boolean;
        data: CheckoutSessionResponse;
      }) => response.data,
    }),

    // Get subscription status
    getSubscriptionStatus: builder.query<SubscriptionStatus, void>({
      query: () => ({
        url: '/subscription/status',
        method: 'GET',
      }),
      transformResponse: (response: {
        success: boolean;
        data: SubscriptionStatus;
      }) => response.data,
      providesTags: ['Subscription'],
    }),

    // Cancel subscription
    cancelSubscription: builder.mutation<void, void>({
      query: () => ({
        url: '/subscription/cancel',
        method: 'POST',
      }),
      invalidatesTags: ['Subscription', 'User'],
    }),

    // Upgrade to investor
    upgradeToInvestor: builder.mutation<Subscription, string>({
      query: (paymentMethodId: string) => ({
        url: '/subscription/upgrade',
        method: 'POST',
        data: { paymentMethodId },
      }),
      invalidatesTags: ['Subscription', 'User'],
    }),

    // Check loan program access
    checkLoanProgramAccess: builder.query<LoanProgramAccessResponse, string>({
      query: (loanProgram: string) => ({
        url: `/subscription/check-access/${loanProgram}`,
        method: 'GET',
      }),
      transformResponse: (response: LoanProgramAccessResponse) => response,
    }),

    getAdminSubscriptions: builder.query<
      AdminSubscriptionRow[],
      { status?: string } | void
    >({
      query: (params) => ({
        url: '/admin/subscriptions',
        method: 'GET',
        params: params?.status ? { status: params.status } : undefined,
      }),
      transformResponse: (response: {
        success: boolean;
        data: AdminSubscriptionRow[];
      }) => response.data,
      providesTags: ['Subscription'],
    }),

    grantAdminSubscription: builder.mutation<
      Subscription,
      { id: string; note?: string }
    >({
      query: ({ id, note }) => ({
        url: `/admin/subscriptions/${id}/grant`,
        method: 'POST',
        data: { note },
      }),
      transformResponse: (response: { success: boolean; data: Subscription }) =>
        response.data,
      invalidatesTags: ['Subscription'],
    }),

    denyAdminSubscription: builder.mutation<
      Subscription,
      { id: string; note?: string }
    >({
      query: ({ id, note }) => ({
        url: `/admin/subscriptions/${id}/deny`,
        method: 'POST',
        data: { note },
      }),
      transformResponse: (response: { success: boolean; data: Subscription }) =>
        response.data,
      invalidatesTags: ['Subscription'],
    }),
  }),
});

export const {
  useCreateCheckoutSessionMutation,
  useGetSubscriptionStatusQuery,
  useCancelSubscriptionMutation,
  useUpgradeToInvestorMutation,
  useCheckLoanProgramAccessQuery,
  useGetAdminSubscriptionsQuery,
  useGrantAdminSubscriptionMutation,
  useDenyAdminSubscriptionMutation,
} = subscriptionApi;
